import React, { useState } from 'react';
import { Tabs, Row, Col, Space, Typography, Button, Switch } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import AttributeTable from './AttributeTableModal';
import MapPortalService from '../../../../services/MapPortal/mapportal.service';
import Spinner from '../../../../shared/components/Spinner';
import { useMutation } from '@tanstack/react-query';
import './QuerySettingPage.scss';

const { TabPane } = Tabs;
const { Text } = Typography;

interface SettingsPageProps {
  goBack: () => void;
  layers: any[];
  toolType: string;
  setColumns: (value: any) => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ goBack, layers, toolType, setColumns }) => {
  const [activeTab, setActiveTab] = useState<string>('layers');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [features, setFeatures] = useState<any>([]);
  const [featureId, setFeatureId] = useState<any>({});
  const [querySelectedColumns, setQuerySelectedColumns] = useState<any>([]);
  const [proximitySelectedColumns, setProximitySelectedColumns] = useState<any>([]);

  const { fetchFeaturesFromLayer, editFeatures } = MapPortalService();
  const { mutateAsync: fetchFeaturesAsync, isPending: fetchingFeatures } = useMutation(fetchFeaturesFromLayer());
  const { mutateAsync: editFeaturesAsync, isPending: editingFeatures } = useMutation(editFeatures());

  const columns = features.length && Object.keys(features?.[0]?.properties);

  const visibleLayers = layers.filter((layer) => !layer.isHidden);
  const hiddenLayers = layers.filter((layer) => layer.isHidden);

  const handleApply = async (selected: string[]) => {
    await editFeaturesAsync({
      featureId,
      toolType,
      selectedColumns: selected,
    });
  };

  const handleTableClick = async (value: boolean, layerName: string) => {
    setIsModalVisible(value);
    const features = await fetchFeaturesAsync({ layerName });
    setFeatures(features?.features);

    setColumns((prev: any) => ({
      querySelectedColumns: [...prev?.querySelectedColumns, querySelectedColumns],
      proximitySelectedColumns: [...prev?.proximitySelectedColumns, proximitySelectedColumns],
    }));

    if (toolType === 'proximity') {
      setProximitySelectedColumns(features?.proximity_selected_columns);
    } else {
      setQuerySelectedColumns(features?.query_selected_columns);
    }
    setFeatureId(features?.id);
  };

  const renderLayerRow = (layerName: string, isChecked: boolean = false, isDisabled: boolean = false) => (
    <Row justify="space-between" align="middle" className="query-setting__layer-row">
      <Col flex="auto">
        <Space>
          <Switch defaultChecked={isChecked} disabled={isDisabled} />
          <Text className="toolbox-text query-setting__layer-name">{layerName}</Text>
        </Space>
      </Col>
      <Col>
        <Space>
          <Button disabled={isDisabled} onClick={() => handleTableClick(true, layerName)}>
            Filter by Field
          </Button>
        </Space>
      </Col>
    </Row>
  );

  return (
    <>
      <Spinner isLoading={fetchingFeatures || editingFeatures} />
      <Space direction="vertical" size="small" className="query-setting__container">
        {/* Back Arrow Button */}
        <Button type="text" icon={<LeftOutlined />} onClick={goBack} className="query-setting__back">
          Back
        </Button>

        {/* Tabs for Layers and Hidden Layers */}
        <Tabs className="custom-tabs" activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
          <TabPane tab="Layers" key="layers">
            {visibleLayers.map((layer) => renderLayerRow(layer.uniqueLayerName, true))}
          </TabPane>

          <TabPane tab="Hidden Layers" key="hidden">
            {hiddenLayers.map((layer) => renderLayerRow(layer.uniqueLayerName, false, true))}
          </TabPane>
        </Tabs>

        {features.length > 0 && (
          <AttributeTable
            data={features}
            columns={columns}
            isModalVisible={isModalVisible}
            onApply={handleApply}
            setIsModalVisible={setIsModalVisible}
            selectedColumns={toolType === 'query' ? querySelectedColumns : proximitySelectedColumns}
            setSelectedColumns={toolType === 'query' ? setQuerySelectedColumns : setProximitySelectedColumns}
          />
        )}
      </Space>
    </>
  );
};

export default SettingsPage;
